import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, Building2, User } from 'lucide-react';
import { Property, ExtractedTransaction } from '../types';
import { processDocumentTransactions, assignTransactionsToProperties } from '../utils/openai';
import { documentUploadService, enhancedTransactionService, propertyService } from '../services/database';
import { formatCurrency } from '../utils/helpers';
import { createWorker } from 'tesseract.js';

interface DocumentUploadProps {
  onTransactionsAdded: (transactions: any[]) => void;
  onClose: () => void;
}

interface ProcessedTransaction extends ExtractedTransaction {
  property_id?: string;
  property_name?: string;
  isEditing?: boolean;
  currency: string;
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onTransactionsAdded,
  onClose,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [extractedTransactions, setExtractedTransactions] = useState<ProcessedTransaction[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [currentStep, setCurrentStep] = useState<'upload' | 'review' | 'assign' | 'complete'>('upload');
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // PDF text extraction using a simpler approach
  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      // For now, we'll provide a user-friendly message about PDF processing
      // In a production environment, you'd want to implement proper PDF.js with worker setup
      return `PDF Document: ${file.name}\n\nPDF processing is currently being set up. For now, please:\n1. Convert your PDF to CSV format using your bank's export feature\n2. Or copy the text content and paste it into a .txt file\n3. Or take a screenshot and save as PNG/JPG for OCR processing\n\nThis will be fully automated in the next update.`;
    } catch (error) {
      console.error('PDF extraction error:', error);
      throw new Error('Failed to extract text from PDF. Please convert to CSV or text format for now.');
    }
  };

  // Image OCR using Tesseract.js
  const extractTextFromImage = async (file: File): Promise<string> => {
    try {
      const worker = await createWorker('eng');
      const { data: { text } } = await worker.recognize(file);
      await worker.terminate();
      
      return text.trim();
    } catch (error) {
      console.error('OCR extraction error:', error);
      throw new Error('Failed to extract text from image. Please ensure the image is clear and contains readable text.');
    }
  };

  // Load properties on component mount
  React.useEffect(() => {
    const loadProperties = async () => {
      const props = await propertyService.getProperties();
      setProperties(props);
    };
    loadProperties();
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = [
        'text/csv', 
        'text/plain',
        'image/png',
        'image/jpeg',
        'image/jpg'
      ];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Please select a CSV, text, PNG, or JPG file (PDF support coming soon)');
        return;
      }
      
      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const extractTextFromFile = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (file.type === 'text/csv' || file.type === 'text/plain') {
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          resolve(text);
        };
        reader.onerror = reject;
        reader.readAsText(file);
      } else if (file.type.startsWith('image/')) {
        // For image files, we'll use OCR
        extractTextFromImage(file).then(resolve).catch(reject);
      } else {
        reject(new Error('Unsupported file type'));
      }
    });
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Extract text from file
      const documentText = await extractTextFromFile(file);

      // Create document upload record
      const uploadData = {
        user_id: 'user1', // This should come from auth context
        filename: file.name,
        file_size: file.size,
        file_type: file.type,
        upload_date: new Date().toISOString(),
        processing_status: 'processing' as const,
      };

      const uploadRecord = await documentUploadService.createDocumentUpload(uploadData);
      if (!uploadRecord) {
        throw new Error('Failed to create upload record');
      }

      setProcessing(true);
      setCurrentStep('review');

      // Process document with AI
      const processedData = await processDocumentTransactions(documentText, properties);
      
      // Assign transactions to properties
      const propertyAssignments = await assignTransactionsToProperties(
        processedData.transactions.map(t => ({
          description: t.description,
          category: t.category,
          classification: t.classification,
        })),
        properties
      );

      // Combine processed transactions with property assignments
      const combinedTransactions: ProcessedTransaction[] = processedData.transactions.map((transaction, index) => {
        const assignment = propertyAssignments.property_suggestions[index];
        const property = properties.find(p => p.id === assignment?.suggested_property_id);
        
        return {
          ...transaction,
          property_id: assignment?.suggested_property_id,
          property_name: property?.address,
          currency: 'USD',
        };
      });

      setExtractedTransactions(combinedTransactions);

      // Update upload record with extracted transactions
      await documentUploadService.updateDocumentUpload(uploadRecord.id, {
        processing_status: 'completed',
        extracted_transactions: combinedTransactions,
      });

      setProcessing(false);
      setCurrentStep('assign');

    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process document');
      setUploading(false);
      setProcessing(false);
    }
  };

  const handlePropertyChange = (transactionIndex: number, propertyId: string | undefined) => {
    const updatedTransactions = [...extractedTransactions];
    const property = properties.find(p => p.id === propertyId);
    
    updatedTransactions[transactionIndex] = {
      ...updatedTransactions[transactionIndex],
      property_id: propertyId,
      property_name: property?.address,
    };

    setExtractedTransactions(updatedTransactions);
  };

  const handleSaveTransactions = async () => {
    setProcessing(true);
    setError('');

    try {
      const transactionsToSave = extractedTransactions.map(transaction => ({
        user_id: 'user1', // This should come from auth context
        property_id: transaction.property_id,
        amount: transaction.type === 'expense' ? -Math.abs(transaction.amount) : Math.abs(transaction.amount),
        type: transaction.type,
        category: transaction.category,
        description: transaction.description,
        classification: transaction.classification,
        date: transaction.date,
        currency: 'USD',
      }));

      const savedTransactions = await enhancedTransactionService.bulkCreateTransactions(transactionsToSave);
      
      if (savedTransactions.length > 0) {
        onTransactionsAdded(savedTransactions);
        setCurrentStep('complete');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save transactions');
    } finally {
      setProcessing(false);
    }
  };

  const getTransactionIcon = (type: 'income' | 'expense') => {
    return type === 'income' ? (
      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-4 h-4 text-green-600" />
      </div>
    ) : (
      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
        <AlertCircle className="w-4 h-4 text-red-600" />
      </div>
    );
  };

  const getClassificationIcon = (classification: 'personal' | 'business') => {
    return classification === 'business' ? (
      <Building2 className="w-4 h-4 text-blue-600" />
    ) : (
      <User className="w-4 h-4 text-gray-600" />
    );
  };

  if (currentStep === 'complete') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Upload Complete!</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Successfully processed {extractedTransactions.length} transactions from your document.
            </p>
            <button
              onClick={onClose}
              className="w-full bg-brand-600 text-white py-2 px-4 rounded-lg hover:bg-brand-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Upload Document</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {currentStep === 'upload' && (
          <div className="space-y-6">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Upload your document</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Supported formats: CSV, text, PNG, or JPG files. We'll automatically extract and categorize transactions. (PDF support coming soon)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.png,.jpg,.jpeg"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-brand-600 text-white py-2 px-4 rounded-lg hover:bg-brand-700 transition-colors"
              >
                Select File
              </button>
              {file && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-2" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                  </div>
                </div>
              )}
            </div>

            {file && (
              <div className="flex justify-end">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-brand-600 text-white py-2 px-6 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    'Process Document'
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {currentStep === 'review' && processing && (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-brand-600 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Processing Document</h3>
            <p className="text-gray-600 dark:text-gray-400">AI is analyzing your document and extracting transactions...</p>
          </div>
        )}

        {currentStep === 'assign' && extractedTransactions.length > 0 && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Review & Assign Transactions ({extractedTransactions.length})
              </h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {extractedTransactions.filter(t => t.classification === 'business').length} business,
                {extractedTransactions.filter(t => t.classification === 'personal').length} personal
              </div>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {extractedTransactions.map((transaction, index) => (
                <div
                  key={index}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {getTransactionIcon(transaction.type)}
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{transaction.description}</span>
                          {getClassificationIcon(transaction.classification)}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span>{transaction.category}</span>
                          <span>{transaction.date}</span>
                          <span>Confidence: {Math.round(transaction.confidence * 100)}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-semibold ${transaction.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(Math.abs(transaction.amount))}
                      </div>
                    </div>
                  </div>

                  {transaction.classification === 'business' && (
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <select
                        value={transaction.property_id || ''}
                        onChange={(e) => handlePropertyChange(index, e.target.value || undefined)}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Select Property</option>
                        {properties.map(property => (
                          <option key={property.id} value={property.id}>
                            {property.address}
                          </option>
                        ))}
                      </select>
                      {transaction.property_name && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          → {transaction.property_name}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTransactions}
                disabled={processing}
                className="bg-brand-600 text-white py-2 px-6 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Transactions'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 